import { FileText, GitCommit } from 'lucide-react';
import { Kicker } from '@/design-system/Kicker';
import {
  actionStateLabel,
  findEntry,
  type ActionRef,
  type ActionState,
} from '@/versions/v1/shared/meeting-log';

// Drawer body for a single meeting citation. Opened from the v2 changelog
// scene's actor chips and action chips. Shows verbatim quote + action refs +
// shipped state + pointers back to the meeting source files.

type Data = {
  meetingId?: string;
  entryId?: string;
};

const STATE_TONE: Record<ActionState, { background: string; foreground: string }> = {
  shipped: { background: 'rgba(46,164,79,0.14)', foreground: 'var(--state-online)' },
  'in-flight': { background: 'rgba(232,116,60,0.14)', foreground: 'var(--accent-ore)' },
  decided: { background: 'rgba(96,165,250,0.14)', foreground: 'var(--state-info)' },
  planned: { background: 'var(--hairline)', foreground: 'var(--ink-tertiary)' },
  blocked: { background: 'rgba(229,72,77,0.14)', foreground: 'var(--state-blocked)' },
};

function StatePill({ state }: { state: ActionState }) {
  const tone = STATE_TONE[state];
  return (
    <span
      className="tabular"
      style={{
        background: tone.background,
        color: tone.foreground,
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '2px 6px',
        borderRadius: 3,
      }}
    >
      {actionStateLabel(state)}
    </span>
  );
}

function ActionCard({ action }: { action: ActionRef }) {
  return (
    <div
      style={{
        border: '1px solid var(--hairline)',
        borderRadius: 6,
        padding: 12,
        background: 'var(--surface-2)',
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span
          className="tabular"
          style={{ color: 'var(--ink-tertiary)', fontSize: 11 }}
        >
          {action.id}
        </span>
        <StatePill state={action.state} />
      </div>
      <div
        style={{
          color: 'var(--ink-primary)',
          fontSize: 13,
          lineHeight: 1.4,
        }}
      >
        {action.title}
      </div>
      {action.state === 'shipped' && (action.shippedIn || action.shippedCommit) && (
        <div
          className="flex items-center gap-3 mt-2"
          style={{ color: 'var(--ink-tertiary)', fontSize: 11 }}
        >
          {action.shippedIn && (
            <span>
              Shipped in <span style={{ color: 'var(--ink-secondary)' }}>{action.shippedIn}</span>
            </span>
          )}
          {action.shippedCommit && (
            <span className="inline-flex items-center gap-1">
              <GitCommit size={11} aria-hidden="true" />
              <span className="tabular">{action.shippedCommit}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function MeetingCitationDrawerBody({ data }: { data: Data }) {
  const { meetingId, entryId } = data;
  const lookup = meetingId && entryId ? findEntry(meetingId, entryId) : null;

  if (!lookup) {
    return (
      <div className="flex-1 p-5 overflow-y-auto scroll-soft">
        <div style={{ color: 'var(--ink-tertiary)', fontSize: 12 }}>
          Citation not found — {meetingId ?? '?'} / {entryId ?? '?'}
        </div>
      </div>
    );
  }

  const { meeting, entry } = lookup;
  const primaryActor = entry.actors[0] ?? 'Unknown';

  return (
    <div className="flex-1 px-5 py-4 overflow-y-auto scroll-soft" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Meeting metadata */}
      <div>
        <Kicker>{meeting.date}</Kicker>
        <div
          style={{
            marginTop: 4,
            color: 'var(--ink-primary)',
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          {meeting.title}
        </div>
        <div
          className="mt-1"
          style={{ color: 'var(--ink-tertiary)', fontSize: 11 }}
        >
          {meeting.attendees.join(' · ')}
        </div>
      </div>

      {/* Verbatim quote */}
      <blockquote
        style={{
          margin: 0,
          padding: '12px 14px',
          borderLeft: '3px solid var(--accent-ore)',
          background: 'var(--surface-2)',
          borderRadius: 4,
          color: 'var(--ink-primary)',
          fontSize: 13,
          lineHeight: 1.6,
          fontStyle: 'italic',
        }}
      >
        “{entry.quote}”
        <div
          className="mt-2"
          style={{
            fontStyle: 'normal',
            color: 'var(--ink-tertiary)',
            fontSize: 11,
            textAlign: 'right',
          }}
        >
          — {primaryActor}
          {entry.actors.length > 1 && (
            <span> · with {entry.actors.slice(1).join(', ')}</span>
          )}
        </div>
      </blockquote>

      {/* Action refs */}
      <div>
        <Kicker>Action{entry.actions.length > 1 ? 's' : ''}</Kicker>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {entry.actions.map((action) => (
            <ActionCard key={action.id} action={action} />
          ))}
        </div>
      </div>

      {/* References */}
      <div>
        <Kicker>References</Kicker>
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '8px 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <li
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--ink-secondary)',
              fontSize: 12,
            }}
          >
            <FileText size={12} aria-hidden="true" />
            <span style={{ color: 'var(--ink-tertiary)' }}>Cleaned input:</span>
            <span className="tabular">{meeting.inputPath}</span>
          </li>
          <li
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--ink-secondary)',
              fontSize: 12,
            }}
          >
            <FileText size={12} aria-hidden="true" />
            <span style={{ color: 'var(--ink-tertiary)' }}>Action file:</span>
            <span className="tabular">{meeting.actionsPath}</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
